import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'featured' | 'compact';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="block group cursor-pointer"
      >
        <article className="flex gap-4 p-3 rounded-lg hover:bg-background-100 transition-colors">
          <div className="w-20 h-20 flex-shrink-0 rounded-md overflow-hidden">
            <img
              src={article.imageUrl}
              alt={article.title}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-medium text-foreground-800 group-hover:text-primary-500 transition-colors line-clamp-2">
              {article.title}
            </h3>
            <div className="flex items-center gap-3 mt-1.5 text-xs text-foreground-500">
              <span>{article.publishDate}</span>
              <span>{article.readTime}</span>
            </div>
          </div>
        </article>
      </Link>
    );
  }

  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="block group cursor-pointer"
      >
        <article className="flex flex-col lg:flex-row gap-6 p-5 rounded-xl bg-background-50 border border-background-200/60 hover:border-primary-200 transition-all">
          <div className="w-full lg:w-72 flex-shrink-0 h-48 lg:h-44 rounded-lg overflow-hidden">
            <img
              src={article.imageUrl}
              alt={article.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          </div>
          <div className="flex-1 flex flex-col justify-center">
            <div className="flex items-center gap-3 flex-wrap mb-2">
              <span className="text-xs px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 font-medium whitespace-nowrap">
                {article.categoryId === 'cat-1' ? '基础入门' :
                 article.categoryId === 'cat-2' ? '作品解析' :
                 article.categoryId === 'cat-3' ? '文化背景' :
                 article.categoryId === 'cat-4' ? '分类盘点' : '资源索引'}
              </span>
              <span className="text-xs text-foreground-500">{article.publishDate}</span>
              <span className="text-xs text-foreground-500">{article.readTime}</span>
            </div>
            <h3 className="text-lg font-heading font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2">
              {article.title}
            </h3>
            <p className="text-sm text-foreground-600 mt-2 line-clamp-2 leading-relaxed">
              {article.excerpt}
            </p>
            <div className="flex items-center gap-2 mt-3 flex-wrap">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-xs px-2 py-0.5 rounded bg-secondary-100 text-secondary-700 whitespace-nowrap"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </article>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.slug}`}
      className="block group cursor-pointer"
    >
      <article className="rounded-xl bg-background-50 border border-background-200/60 overflow-hidden hover:border-primary-200 transition-all">
        <div className="h-48 overflow-hidden">
          <img
            src={article.imageUrl}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="p-5">
          <div className="flex items-center gap-3 flex-wrap mb-2">
            <span className="text-xs px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 font-medium whitespace-nowrap">
              {article.categoryId === 'cat-1' ? '基础入门' :
               article.categoryId === 'cat-2' ? '作品解析' :
               article.categoryId === 'cat-3' ? '文化背景' :
               article.categoryId === 'cat-4' ? '分类盘点' : '资源索引'}
            </span>
            <span className="text-xs text-foreground-500">{article.readTime}</span>
          </div>
          <h3 className="text-base font-heading font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2">
            {article.title}
          </h3>
          <p className="text-sm text-foreground-600 mt-2 line-clamp-2 leading-relaxed">
            {article.excerpt}
          </p>
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            {article.tags.slice(0, 2).map((tag) => (
              <span
                key={tag}
                className="text-xs px-2 py-0.5 rounded bg-secondary-100 text-secondary-700 whitespace-nowrap"
              >
                {tag}
              </span>
            ))}
          </div>
          <div className="mt-3 pt-3 border-t border-background-200/60 flex items-center justify-between">
            <span className="text-xs text-foreground-500">{article.publishDate}</span>
            <span className="text-xs text-primary-500 font-medium flex items-center gap-1 whitespace-nowrap">
              阅读全文
              <i className="ri-arrow-right-line w-3 h-3 flex items-center justify-center"></i>
            </span>
          </div>
        </div>
      </article>
    </Link>
  );
}